import { Button, Card, MonoLabel } from '@/design-system';
import type { AutoScheduleResult, PlanningRow } from '@/entities/order';
import { formatDayTimeShort } from '@/shared/lib';
import { CardTitle } from '@/shared/ui';

import { planningCopy } from '../copy';

export interface AutoSchedulePanelProps {
    preview: AutoScheduleResult;
    rows: readonly PlanningRow[];
    applying: boolean;
    onApply: () => void;
    onClose: () => void;
}

/**
 * Eerst kijken, dan doen: de voorvertoning van automatisch inplannen
 * (`dry_run`) met per keuring wie, wanneer en hoeveel kilometer het kost, en
 * wat er blijft liggen met de reden van de API.
 */
export function AutoSchedulePanel({
    preview,
    rows,
    applying,
    onApply,
    onClose,
}: AutoSchedulePanelProps) {
    const nameOf = (uuid: string | null) =>
        rows.find((row) => row.inspectorUuid === uuid)?.name ?? planningCopy.autoUnknownInspector;

    return (
        <Card padding="md" as="section" aria-labelledby="auto-schedule-title" className="mb-[14px]">
            <CardTitle as="h2" className="mb-1">
                <span id="auto-schedule-title">{planningCopy.autoTitle}</span>
            </CardTitle>
            <p className="m-0 mb-3 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {preview.scheduled.length === 0 && preview.skipped.length === 0
                    ? planningCopy.autoNothing
                    : planningCopy.autoIntro}
            </p>

            {preview.scheduled.length > 0 ? (
                <>
                    <MonoLabel size={10} tone="muted" as="div" className="mb-1">
                        {planningCopy.autoScheduledTitle}
                    </MonoLabel>
                    <ul className="m-0 mb-3 list-none p-0">
                        {preview.scheduled.map((row) => (
                            <li
                                key={row.orderUuid}
                                className="border-b border-[var(--border-subtle-2)] py-[7px] text-[13px] text-[var(--text-primary)]"
                            >
                                {planningCopy.autoLine(
                                    row.orderNumber,
                                    nameOf(row.inspectorUuid),
                                    formatDayTimeShort(row.scheduledAt),
                                    Math.round(row.addedKm),
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            ) : null}

            {preview.skipped.length > 0 ? (
                <>
                    <MonoLabel size={10} tone="muted" as="div" className="mb-1">
                        {planningCopy.autoSkippedTitle}
                    </MonoLabel>
                    <ul className="m-0 mb-3 list-none p-0">
                        {preview.skipped.map((row) => (
                            <li
                                key={row.orderUuid}
                                className="border-b border-[var(--border-subtle-2)] py-[7px] text-[13px] text-[var(--text-secondary)]"
                            >
                                <span className="font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-primary)]">
                                    {row.orderNumber}
                                </span>
                                {` · ${row.reason}`}
                            </li>
                        ))}
                    </ul>
                </>
            ) : null}

            <div className="flex flex-wrap gap-2">
                {preview.scheduled.length > 0 ? (
                    <Button variant="primary" size="sm" disabled={applying} onClick={onApply}>
                        {applying
                            ? planningCopy.autoApplying
                            : planningCopy.autoApply(preview.scheduled.length)}
                    </Button>
                ) : null}
                <Button variant="outline" size="sm" onClick={onClose}>
                    {planningCopy.autoClose}
                </Button>
            </div>
        </Card>
    );
}
